// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

// Lớp mỏng trên livekit-client.
//
// Dùng SDK lõi thay vì @livekit/components-react: bộ component dựng sẵn mang
// theo giao diện riêng của nó, và mọi thứ ở đây sẽ thành cuộc chiến ghi đè CSS.
// Phần thật sự khó của WebRTC — thương lượng, adaptive stream, simulcast — nằm
// trong SDK lõi, và ta dùng nguyên nó.

import { useCallback, useEffect, useRef, useState } from 'react'
import { parseParticipantMeta } from './api'
import {
  ConnectionState,
  RoomEvent,
  Track,
  createLocalTracks,
  Room,
  type LocalTrack,
  type LocalVideoTrack,
  type RemoteTrack,
  type Participant,
} from 'livekit-client'

export type Tile = {
  id: string
  /**
   * identity của LiveKit. KHÁC `id`: một người đang chia sẻ màn hình có hai ô,
   * và cả hai cùng một identity. Mọi thứ gắn với CON NGƯỜI chứ không gắn với ô
   * — cờ ghi hình cục bộ chẳng hạn — phải tra theo trường này.
   */
  identity: string
  name: string
  isLocal: boolean
  speaking: boolean
  video?: MediaStreamTrack
  audio?: MediaStreamTrack
  muted: boolean
  camOff: boolean
  /** Ô này là màn hình đang chia sẻ, không phải camera. */
  isScreen: boolean

  /** participant_id, đọc từ metadata của LiveKit — xem api.parseParticipantMeta. */
  pid: string | null
  role: string
}

export type RoomState = {
  tiles: Tile[]
  micOn: boolean
  camOn: boolean
  screenOn: boolean
  /** connected | reconnecting | disconnected — điều khiển thông báo cho người dùng. */
  connection: ConnectionState
  /** mediaLost khác "đã rời phòng": xem ghi chú ở leave(). */
  mediaLost: boolean
}

export function useRoom() {
  const roomRef = useRef<Room | null>(null)
  // Track camera của chính mình, giữ riêng để gắn bộ xử lý nền vào.
  const localVideoRef = useRef<LocalVideoTrack | null>(null)
  // version buộc React render lại khi roomRef đổi — ref không kích hoạt render,
  // và chat/nền cần biết Room đã sẵn sàng.
  const [version, bump] = useState(0)
  const [state, setState] = useState<RoomState>({
    tiles: [],
    micOn: false,
    camOn: false,
    screenOn: false,
    connection: ConnectionState.Disconnected,
    mediaLost: false,
  })

  // snapshot dựng lại toàn bộ danh sách ô từ trạng thái HIỆN TẠI của room.
  //
  // Dựng lại thay vì vá từng sự kiện: sự kiện của WebRTC đến không theo thứ tự
  // ổn định, và một danh sách được vá dần sẽ lệch khỏi thực tế theo những cách
  // chỉ lộ ra sau mười phút họp.
  const snapshot = useCallback(() => {
    const room = roomRef.current
    if (!room) return

    // Màn hình chia sẻ là một track RIÊNG, không phải camera thay thế
    // (media.TrackScreen). Nó thành một ô riêng: người đang trình bày vẫn phải
    // nhìn thấy được mặt, và người xem cần cả hai.
    const screenTile = (p: Participant, isLocal: boolean): Tile | null => {
      const pub = [...p.trackPublications.values()].find(
        (t) => t.source === Track.Source.ScreenShare,
      )
      if (!pub || pub.isMuted || !pub.track) return null
      const meta = parseParticipantMeta(p.metadata)
      return {
        id: p.identity + ':screen',
        identity: p.identity,
        name: p.name || p.identity,
        isLocal,
        speaking: false,
        video: pub.track.mediaStreamTrack,
        muted: true,
        camOff: false,
        isScreen: true,
        pid: meta?.pid ?? null,
        role: meta?.role ?? 'attendee',
      }
    }

    const tileFor = (p: Participant, isLocal: boolean): Tile => {
      const pubs = [...p.trackPublications.values()]
      const video = pubs.find((t) => t.source === Track.Source.Camera)
      const audio = pubs.find((t) => t.source === Track.Source.Microphone)
      const meta = parseParticipantMeta(p.metadata)
      return {
        id: p.identity,
        identity: p.identity,
        name: p.name || p.identity,
        isLocal,
        speaking: p.isSpeaking,
        // Track đang tắt vẫn còn publication; chỉ gắn vào thẻ <video> khi nó
        // thật sự đang chạy, nếu không ô hiện một khung hình đứng im.
        video: video && !video.isMuted ? video.track?.mediaStreamTrack : undefined,
        audio: isLocal ? undefined : audio?.track?.mediaStreamTrack,
        muted: !audio || audio.isMuted,
        camOff: !video || video.isMuted,
        isScreen: false,
        pid: meta?.pid ?? null,
        role: meta?.role ?? 'attendee',
      }
    }

    setState((s) => ({
      ...s,
      // Màn hình đứng TRƯỚC: nó là thứ mọi người đang nhìn, và đẩy nó xuống
      // cuối lưới nghĩa là người vào sau phải cuộn để thấy nội dung buổi họp.
      tiles: [
        screenTile(room.localParticipant, true),
        ...[...room.remoteParticipants.values()].map((p) => screenTile(p, false)),
        tileFor(room.localParticipant, true),
        ...[...room.remoteParticipants.values()].map((p) => tileFor(p, false)),
      ].filter((t): t is Tile => t !== null),
      micOn: room.localParticipant.isMicrophoneEnabled,
      camOn: room.localParticipant.isCameraEnabled,
      screenOn: room.localParticipant.isScreenShareEnabled,
      connection: room.state,
    }))
  }, [])

  /**
   * connect nối vào phòng và phát những track mà người dùng ĐÃ CHỌN ở prejoin.
   *
   * `devices` mặc định bật cả hai để mọi lời gọi cũ giữ nguyên hành vi.
   *
   * ⚠️ Không xin quyền cho thiết bị đang tắt. `createLocalTracks({video: false})`
   * không chạm tới camera, nên người vào phòng với camera tắt KHÔNG thấy hộp
   * thoại xin quyền camera — và đó là nửa quan trọng của việc có prejoin. Xin
   * quyền cho một thiết bị rồi không dùng là cách nhanh nhất để người dùng bấm
   * "Chặn" một lần rồi không bao giờ bật lại được.
   */
  const connect = useCallback(
    async (
      url: string,
      token: string,
      _name: string,
      devices: { mic: boolean; cam: boolean } = { mic: true, cam: true },
    ) => {
      const room = new Room({ adaptiveStream: true, dynacast: true })
      roomRef.current = room

      const redraw = () => snapshot()
      room
        .on(RoomEvent.TrackSubscribed, redraw)
        .on(RoomEvent.TrackUnsubscribed, (t: RemoteTrack) => {
          t.detach()
          redraw()
        })
        .on(RoomEvent.LocalTrackPublished, redraw)
        .on(RoomEvent.LocalTrackUnpublished, (p) => {
          ;(p.track as LocalTrack | undefined)?.detach()
          redraw()
        })
        .on(RoomEvent.ParticipantConnected, redraw)
        .on(RoomEvent.ParticipantDisconnected, redraw)
        .on(RoomEvent.TrackMuted, redraw)
        .on(RoomEvent.TrackUnmuted, redraw)
        .on(RoomEvent.ActiveSpeakersChanged, redraw)
        .on(RoomEvent.ConnectionStateChanged, redraw)
        .on(RoomEvent.ParticipantMetadataChanged, redraw)
        .on(RoomEvent.LocalTrackPublished, redraw)
        // ⚠️ Mất kết nối media KHÔNG phải là rời cuộc họp (ADR-0001 §8).
        //
        // Session vẫn sống ở control plane, dòng participant vẫn là admitted, và
        // host vẫn phải duyệt/đuổi/khoá được. Xoá sạch phiên ở đây nghĩa là một
        // host bị chặn UDP mất hết quyền điều hành đúng lúc họ cần nó nhất.
        .on(RoomEvent.Disconnected, () => {
          setState((s) => ({ ...s, mediaLost: true, tiles: [] }))
        })

      await room.connect(url, token)

      // ⚠️ KHÔNG gọi setName ở đây.
      //
      // Token đã mang sẵn tên (media.Grant.Name), nên lời gọi đó thừa — và nó
      // ném `SignalRequestError: does not have permission to update own
      // metadata`, vì Z-Meet cố tình KHÔNG cấp canUpdateOwnMetadata: metadata
      // của participant mang pid và role, và cho client tự ghi vào đó là cho
      // họ tự khai vai trò.
      //
      // Lỗi đó xảy ra TRƯỚC createLocalTracks bên dưới, nên nó nuốt luôn cả
      // việc phát mic và camera. Triệu chứng cực dễ hiểu nhầm: phòng vẫn hiện
      // đủ ô cho mọi người, chỉ là ô nào cũng là chữ cái thay cho hình — trông
      // như mọi người đều tắt camera.

      // Tắt cả hai thì không gọi createLocalTracks: gọi với `{audio: false,
      // video: false}` vẫn là một lượt chạm vào API thiết bị, và Safari hỏi
      // quyền cho một lời gọi không xin gì.
      if (devices.mic || devices.cam) {
        const tracks = await createLocalTracks({
          audio: devices.mic,
          video: devices.cam,
        }).catch(() => [])
        for (const t of tracks) {
          await room.localParticipant.publishTrack(t)
          if (t.kind === Track.Kind.Video) localVideoRef.current = t as LocalVideoTrack
        }
      }
      bump((n) => n + 1)
      snapshot()
    },
    [snapshot],
  )

  const setMic = useCallback(
    async (on: boolean) => {
      await roomRef.current?.localParticipant.setMicrophoneEnabled(on)
      snapshot()
    },
    [snapshot],
  )

  const setCam = useCallback(
    async (on: boolean) => {
      await roomRef.current?.localParticipant.setCameraEnabled(on)
      snapshot()
    },
    [snapshot],
  )

  /**
   * setScreen bật/tắt chia sẻ màn hình.
   *
   * Trả về lỗi khi trình duyệt từ chối hoặc người dùng bấm Huỷ ở hộp thoại
   * chọn màn hình. Người gọi PHẢI phân biệt hai thứ: "huỷ" là hành động bình
   * thường và không được hiện như một lỗi, còn "không hỗ trợ" thì phải nói.
   */
  const setScreen = useCallback(
    async (on: boolean) => {
      const room = roomRef.current
      if (!room) return
      try {
        await room.localParticipant.setScreenShareEnabled(
          on,
          {
            // audio: chia sẻ cả tiếng của tab. Chrome hỏi thêm một ô tick;
            // Firefox và Safari bỏ qua. Không có nó thì một video được trình
            // chiếu ra thành phim câm, và người trình bày không nhận ra.
            audio: true,
            // Slide và mã nguồn cần ĐỘ PHÂN GIẢI, không cần khung hình/giây.
            // Mặc định của LiveKit tối ưu cho chuyển động, nên chữ nhỏ bị nhoè
            // đúng lúc người ta cần đọc nó.
            resolution: { width: 1920, height: 1080, frameRate: 5 },
          },
          { degradationPreference: 'maintain-resolution' },
        )
      } catch (e) {
        // NotAllowedError là người dùng bấm Huỷ — không phải lỗi.
        if ((e as DOMException)?.name !== 'NotAllowedError') throw e
      }
      snapshot()
    },
    [snapshot],
  )

  /**
   * screenSupported cho biết trình duyệt có chia sẻ màn hình được không.
   *
   * Safari trên iOS KHÔNG có getDisplayMedia và sẽ không có — đó là giới hạn
   * của nền tảng. Hiện nút rồi báo lỗi khi bấm là bắt người dùng tự phát hiện
   * điều đó thay cho ta.
   */
  const screenSupported =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function'

  /** leave là rời hẳn — chủ động, có ý thức. Khác hẳn mediaLost. */
  const leave = useCallback(async () => {
    await roomRef.current?.disconnect()
    roomRef.current = null
    localVideoRef.current = null
    bump((n) => n + 1)
    setState({
      tiles: [],
      micOn: false,
      camOn: false,
      screenOn: false,
      connection: ConnectionState.Disconnected,
      mediaLost: false,
    })
  }, [])

  useEffect(() => {
    return () => {
      void roomRef.current?.disconnect()
    }
  }, [])

  return {
    ...state,
    connect,
    setMic,
    setCam,
    setScreen,
    screenSupported,
    leave,
    connected: roomRef.current !== null,
    // Room lộ ra để chat/reaction dùng kênh dữ liệu, và localVideo để gắn bộ
    // xử lý nền. Không bọc thêm một lớp uỷ nhiệm chỉ để chuyển tiếp lời gọi —
    // đó là chỗ bug ẩn vào mà không đổi lại gì.
    room: roomRef.current,
    localVideo: localVideoRef.current,
    // refresh dựng lại danh sách ô mà không cần một sự kiện của LiveKit.
    //
    // Cần cho bộ xử lý nền: setProcessor/stopProcessor đổi
    // LocalVideoTrack.mediaStreamTrack (thành track đã xử lý và ngược lại)
    // nhưng KHÔNG phát ra RoomEvent nào. Không gọi refresh thì ô của chính
    // mình còn giữ track camera thô cho tới khi một sự kiện bất kỳ khác —
    // ai đó nói, ai đó vào phòng — tình cờ chạy snapshot.
    refresh: snapshot,
    _v: version,
  }
}
